import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '../lib/supabase'

export function useWatchRoom(userId) {
  const [room,      setRoom]      = useState(null)
  const [members,   setMembers]   = useState([])
  const [reactions, setReactions] = useState([])
  const [loading,   setLoading]   = useState(false)
  const reactionTimer = useRef({})
  // Set the moment the user intentionally leaves a room. An in-flight realtime
  // event (a members change, or the room UPDATE) can still be delivered in the
  // gap before the channel effect tears down, and would otherwise re-`setRoom`
  // the room we just cleared, snapping the ViewingRoom back open (Jason 2026-07-24).
  const closedRef = useRef(false)

  const loadRoom = useCallback(async (roomId) => {
    if (!roomId || closedRef.current) return null
    const [{ data: r }, { data: m }] = await Promise.all([
      supabase.from('watch_rooms').select('*').eq('id', roomId).single(),
      supabase.from('watch_room_members')
        .select('*, profiles(id,display_name,username)')
        .eq('room_id', roomId).neq('status', 'left'),
    ])
    if (r) setRoom(r)
    if (m) setMembers(m)
    return r || null      // joinRoom hands this back — its own pre-check row is a stub
  }, [])

  const findActiveRoom = useCallback(async () => {
    if (!userId) return null
    const { data, error } = await supabase
      .from('watch_room_members')
      .select('room_id, watch_rooms(*)')
      // A user can legitimately hold more than one non-'left' membership: leaveRoom is only
      // reachable from the lobby, so backgrounding the app or navigating away leaves the row
      // 'joined' forever. maybeSingle() then threw PGRST116 and returned null, permanently
      // killing the rehydrate-my-live-room behaviour for that user. Take the newest instead.
      // ORDER BY joined_at — the members table has NO created_at column, so the old
      // .order('created_at') made PostgREST 400 on EVERY call → data null → rehydrate
      // silently never worked at all (found in the 2026-07-26 flow audit).
      .eq('user_id', userId).neq('status', 'left')
      .order('joined_at', { ascending: false }).limit(1).maybeSingle()
    if (error) { console.warn('[watchroom] findActiveRoom failed', error); return null }
    const wr = data?.watch_rooms
    if (!wr || wr.status === 'done' || wr.status === 'finished') return null
    // Recency: a live co-watch is an evening-scale thing. A 'watching' room from last
    // week must not resurrect on every ShowDetail mount (stale-room trap, 2026-07-26).
    if (new Date(wr.created_at).getTime() < Date.now() - 12 * 3600 * 1000) return null
    return wr
  }, [userId])

  // Find an OPEN lobby room for this show that I can rejoin instead of spawning a
  // duplicate. RLS already scopes watch_rooms SELECT to rooms I host or have a membership
  // in (a 'left' membership still counts), so this returns exactly the rooms relevant to
  // me: my partner's room (I was invited/joined) or one I left. Prefer a room hosted by
  // SOMEONE ELSE (my partner's) with the newest activity, so two people converge on one
  // room instead of each sitting in their own (Jason 2026-07-24).
  // `at` = {season, episode} you're trying to watch, when the caller knows it. Used only
  // to PREFER an exact-episode room; never to exclude one. Convergence is the whole point
  // of this function (two people must land in ONE room), so requiring an episode match
  // would resurrect the "two people, two empty rooms" bug the moment their positions
  // differ by an episode. But silently dropping someone aiming at S2·E5 into a stale
  // S1·E9 lobby is a spoiler risk and just reads as broken (audit 2026-07-26). So: match
  // the episode when such a room exists, otherwise converge as before.
  const findRoomForShow = useCallback(async (showId, at = null) => {
    if (!userId || !showId) return null
    // Include 'watching' too, not just 'lobby': RLS already scopes this to rooms I host or
    // am a member of, so this can only ever return MY relevant rooms. Restricting to 'lobby'
    // meant that once the host hit Start (room → 'watching'), a member who reloaded / came
    // back couldn't rejoin the in-progress room and spawned a fresh SOLO room instead — a
    // direct cause of the "two people, two empty rooms, nobody sees anyone" bug
    // (Jason + Melissa, 2026-07-25). A late rejoin into a 'watching' room is exactly right.
    // Recency guard: only converge into rooms from the last few hours. Without it,
    // "Watch Together" dragged the user into an abandoned lobby/watching room from a
    // previous evening with no obvious way out (the "defaulted to a lobby with Melissa"
    // trap, Jason 2026-07-26). watch_rooms is RLS-scoped to rooms I host or joined.
    const since = new Date(Date.now() - 6 * 3600 * 1000).toISOString()
    const { data } = await supabase
      .from('watch_rooms')
      .select('*')
      .eq('show_id', +showId).in('status', ['lobby', 'watching'])
      .gte('created_at', since)
      .order('created_at', { ascending: false })
    if (!data?.length) return null
    // Preference order: someone else's room for THIS episode > any room for this episode >
    // someone else's room > most recent. Partner-first still wins within each tier, so
    // convergence behaviour is unchanged whenever episodes agree (the common case).
    const sameEp = at?.season && at?.episode
      ? data.filter(r => r.season === at.season && r.episode === at.episode)
      : []
    return sameEp.find(r => r.host_id !== userId) || sameEp[0]
        || data.find(r => r.host_id !== userId) || data[0]
  }, [userId])

  const createRoom = useCallback(async ({ show_id, show_name, show_poster, show_backdrop, season, episode, episode_name }) => {
    if (!userId) throw new Error('Not authenticated')
    closedRef.current = false
    setLoading(true)
    try {
      const { data: r, error } = await supabase
        .from('watch_rooms')
        .insert({ show_id: +show_id, show_name, show_poster, show_backdrop, season, episode, episode_name, host_id: userId, status: 'lobby' })
        .select().single()
      if (error) throw error
      await supabase.from('watch_room_members').insert({ room_id: r.id, user_id: userId, status: 'joined' })
      setRoom(r)
      setMembers([{ room_id: r.id, user_id: userId, status: 'joined', profiles: null }])
      return r
    } finally { setLoading(false) }
  }, [userId])

  // Returns { ok, reason } — NEVER throws, but never silently no-ops either.
  // This is the one entry an INVITE points at, so it's the most likely to reference a room
  // that has since ended, and it was the only path with no liveness guard: findActiveRoom
  // (12h) and findRoomForShow (6h + status) both had one. Joining a 'done' room dropped the
  // user straight into the recap for a session they never attended, and a failed upsert was
  // swallowed entirely — leaving the guest flow spinning on "Joining the room…" forever with
  // no error, no retry and no exit (audit 2026-07-26, M3).
  const JOIN_MAX_AGE_MS = 12 * 60 * 60 * 1000
  const joinRoom = useCallback(async (roomId, guestName) => {
    if (!userId || !roomId) return { ok: false, reason: 'invalid' }
    // Check the room is still joinable BEFORE writing a membership row for it.
    //
    // Via an RPC, NOT a direct select: wr_select scopes rooms to the host and existing
    // MEMBERS, so an invitee who hasn't joined yet cannot see the room at all. A direct
    // select therefore returned null for every guest and this guard reported a perfectly
    // live lobby as missing/ended — you had to be a member to be allowed to check whether
    // you could become one. That broke the external invite entirely (Alyssa, 2026-07-27;
    // my own regression from the 15.729 pre-join guard). watch_room_join_info is SECURITY
    // DEFINER and returns only {id, status, created_at} — enough to judge joinability,
    // nothing about the show, host or members.
    const { data: infoRows, error: rErr } = await supabase
      .rpc('watch_room_join_info', { p_room_id: roomId })
    if (rErr) return { ok: false, reason: 'error', error: rErr }
    const r = Array.isArray(infoRows) ? infoRows[0] : infoRows
    if (!r) return { ok: false, reason: 'missing' }
    if (r.status === 'done') return { ok: false, reason: 'ended' }
    if (r.created_at && Date.now() - new Date(r.created_at).getTime() > JOIN_MAX_AGE_MS) {
      return { ok: false, reason: 'stale' }
    }
    closedRef.current = false
    // guestName is set only for anonymous guests (they have no profiles row to read a
    // display name from). upsert only writes the columns provided, so a later re-join
    // without it never clobbers the stored guest_name.
    const row = { room_id: roomId, user_id: userId, status: 'joined' }
    if (guestName) row.guest_name = String(guestName).slice(0, 40)
    const { error: mErr } = await supabase
      .from('watch_room_members').upsert(row, { onConflict: 'room_id,user_id' })
    if (mErr) return { ok: false, reason: 'error', error: mErr }
    // The RPC row is a stub (id/status/created_at only), so hand back the FULL room that
    // loadRoom fetched — callers render show name/poster from it.
    const full = await loadRoom(roomId)
    return { ok: true, room: full || r }
  }, [userId, loadRoom])

  const setReady = useCallback(async (roomId) => {
    await supabase.from('watch_room_members')
      .update({ status: 'ready' })
      .eq('room_id', roomId).eq('user_id', userId)
  }, [userId])

  const startWatching = useCallback(async (roomId) => {
    // NOT host-gated: any present member can start once everyone here is ready. But the
    // direct UPDATE above was silently blocked for non-hosts by wr_update's host-only
    // WITH CHECK (added to stop room hijack) — so a guest's "Start" tap did nothing, the
    // exact bug Jason hit 2026-07-24. The start_watch_room RPC (SECURITY DEFINER, membership
    // -checked) flips the room + all still-present members to 'watching' atomically for any
    // member. Idempotent: it only starts a room still in 'lobby', so a second tap can't
    // reset the shared started_at countdown anchor.
    const { error } = await supabase.rpc('start_watch_room', { p_room: roomId })
    if (error) throw error
  }, [userId])

  const sendReaction = useCallback((roomId, emoji) => {
    ;(async () => {
      try { await supabase.from('watch_room_reactions').insert({ room_id: roomId, user_id: userId, emoji }) } catch {}
    })()
  }, [userId])

  const leaveRoom = useCallback(async (roomId) => {
    if (!roomId) return
    closedRef.current = true
    // leaveRoom is ONLY reachable from the lobby — the live/watching phase exits via
    // endRoom ("Done Watching"). So a leave means the episode was never watched, and the
    // host must NOT flip the room to 'done': that status is what drives the "That was a
    // great watch!" recap, so the old host branch fired a completion cinematic for a
    // lobby nobody watched (Jason 2026-07-24). Everyone — host included — just drops out.
    await supabase.from('watch_room_members')
      .update({ status: 'left' }).eq('room_id', roomId).eq('user_id', userId)
    setRoom(null); setMembers([]); setReactions([])
  }, [userId])

  const endRoom = useCallback(async (roomId) => {
    if (!roomId) return
    // Member-shaped ending (end_watch_room RPC, 2026-07-26): "Done watching" marks the
    // CALLER left, and only the LAST active member out concludes the room ('done').
    // Fixes both directions of the old bug pair: a host tapping Done yanked everyone
    // else into the recap mid-episode (so "host leaves early, partner keeps watching +
    // invites someone new" was impossible), and a remaining guest could never conclude
    // the room at all (wr_update is host-only), leaving zombie 'watching' rooms.
    closedRef.current = true
    const { error } = await supabase.rpc('end_watch_room', { p_room: roomId })
    if (error) {
      // Backstop if the RPC is unreachable: at minimum detach myself.
      console.warn('[watchroom] end_watch_room failed, detaching locally', error)
      await supabase.from('watch_room_members')
        .update({ status: 'left' }).eq('room_id', roomId).eq('user_id', userId)
    }
  }, [userId])

  useEffect(() => {
    if (!room?.id) return
    const ch = supabase.channel(`viewing-room-${room.id}`)
      .on('postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'watch_rooms', filter: `id=eq.${room.id}` },
        // Never resurrect a room the user already left. closedRef flips true
        // BEFORE the leave/end write is even sent (see leaveRoom/endRoom), so it
        // covers the gap an UPDATE could arrive in that `prev` alone can't: prev
        // is only cleared to null AFTER that write resolves, so an event landing
        // during the await was merging into the still-present prev room right up
        // until setRoom(null) ran a tick later (audit sweep, 2026-08-30 — this
        // comment used to describe closedRef as the guard while the code below
        // never actually read it).
        (payload) => setRoom(prev => (prev && !closedRef.current) ? { ...prev, ...payload.new } : prev)
      )
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'watch_room_members', filter: `room_id=eq.${room.id}` },
        () => loadRoom(room.id)
      )
      .on('postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'watch_room_reactions', filter: `room_id=eq.${room.id}` },
        (payload) => {
          const r = payload.new
          const key = `${r.id}-${Date.now()}`
          setReactions(prev => [...prev.slice(-30), {
            id: r.id, key, userId: r.user_id, emoji: r.emoji,
            dx: (Math.random() - 0.5) * 60, ownedBy: r.user_id === userId,
          }])
          reactionTimer.current[key] = setTimeout(() => {
            setReactions(prev => prev.filter(x => x.key !== key))
            delete reactionTimer.current[key]
          }, 2800)
        }
      )
      .subscribe(async (status) => {
        // On (re)subscribe — including after a mobile network drop / cell↔wifi
        // handoff where supabase silently reconnects the socket — refetch the
        // authoritative room + members. postgres_changes does NOT replay events
        // missed during the gap, so without this the participant's room.status and
        // member list freeze at the pre-drop snapshot (e.g. stuck in the lobby while
        // the host has already started or ended the room) until a manual reload
        // (audit 2026-07-06).
        if (status !== 'SUBSCRIBED') return
        loadRoom(room.id)
        // Backfill reactions missed during the same gap. The loadRoom call above
        // exists precisely because postgres_changes doesn't replay missed events —
        // but it only ever covered rooms + members, never reactions, so a partner
        // who left/rejoined (or crossed a wifi↔cell handoff) silently lost every
        // emoji sent while they were away and each side thought the other went
        // quiet (Jason + Melissa live test, 2026-08-04). Short window on purpose:
        // a reaction is a live moment, not history — replaying a minutes-old 😱
        // as if it just happened would read as a ghost. Own reactions are skipped
        // (they already played locally on send).
        const { data: missed } = await supabase.from('watch_room_reactions')
          .select('id, user_id, emoji')
          .eq('room_id', room.id)
          .gte('created_at', new Date(Date.now() - 10000).toISOString())
          .order('created_at', { ascending: true })
          .limit(10)
        for (const r of (missed || [])) {
          if (r.user_id === userId) continue
          const key = `${r.id}-backfill`
          if (reactionTimer.current[key]) continue   // already replayed this one
          setReactions(prev => prev.some(x => x.id === r.id) ? prev : [...prev.slice(-30), {
            id: r.id, key, userId: r.user_id, emoji: r.emoji,
            dx: (Math.random() - 0.5) * 60, ownedBy: false,
          }])
          reactionTimer.current[key] = setTimeout(() => {
            setReactions(prev => prev.filter(x => x.key !== key))
            delete reactionTimer.current[key]
          }, 2800)
        }
      })
    return () => {
      supabase.removeChannel(ch)
      Object.values(reactionTimer.current).forEach(clearTimeout)
    }
  }, [room?.id, loadRoom, userId])

  return { room, members, reactions, loading, createRoom, joinRoom, setReady, startWatching, sendReaction, leaveRoom, endRoom, loadRoom, findActiveRoom, findRoomForShow }
}
